// Plan candidate enumerator — P3 of the Plans & Reasons loop (generate step).
//
// Given an objective gap (the flagship's frame) and the governed candidate
// lever set (the candidate-lever-stack's certified levers, adapted by the API
// composition), this module enumerates every LEGAL candidate path the data
// admits: single levers plus policy-bounded combinations (2–3 levers max).
//
// Laws inherited verbatim from the governed substrate:
// - Candidates come ONLY from admitted levers (an unadmitted candidate cannot
//   exist; the LRP lane's NO-GO was fabricated levers — impossible here by
//   construction).
// - Combination legality is the deconfliction contract: a combination is legal
//   only when every lever draws from a DISTINCT declared evidence family. Two
//   levers sharing a family (or a lever with no declared family) can never be
//   summed — the exclusion is recorded by name, never silently dropped.
// - Max-degradation law (from tradeoff-engine's mergePointSourceClass): a
//   candidate's certainty class is the WEAKEST of its levers' classes; its
//   certainty mix is COUNTED, never estimated.
// - Provenance is the union of the levers' governed refs; every candidate is
//   recoverable to the artifacts/runs that sized it.
//
// This module is pure and deterministic: no I/O, no LLM, no clock.

/**
 * Closed certainty vocabulary, ordered strongest → weakest. Derived from the
 * lever's governed status posture — never an invented "confidence %".
 */
export type PlanCandidateCertaintyClass = 'certified' | 'scenario' | 'modeled';

/** Weakest-first order used by the max-degradation merge. */
export const PLAN_CANDIDATE_CERTAINTY_ORDER: readonly PlanCandidateCertaintyClass[] =
  Object.freeze(['modeled', 'scenario', 'certified']);

/**
 * Closed time-to-impact vocabulary, ordered fastest → slowest. A lever with no
 * governed window carries `null` — a NAMED gap, never a defaulted class.
 */
export type PlanTimeToImpactClass =
  | 'in_quarter'
  | 'two_to_four_quarters'
  | 'beyond_one_year';

export const PLAN_TIME_TO_IMPACT_ORDER: readonly PlanTimeToImpactClass[] =
  Object.freeze(['in_quarter', 'two_to_four_quarters', 'beyond_one_year']);

/** Closed approval-owner vocabulary (exec cabinet officer classes). */
export type PlanApprovalOwnerClass = 'CFO' | 'COO' | 'CHRO' | 'CEO';

export type PlanCandidateConstraintFlag = Readonly<{
  constraintId: string;
  resourceKind: string;
  status: 'compliant' | 'violated' | 'unevaluated';
  /** Evidence backing the constraint bound (ConstraintContract discipline). */
  evidenceRefId: string;
}>;

export type PlanCandidateLeverProvenance = Readonly<{
  /** Stable, redaction-safe proof-ref id for the bound value. */
  evidenceRefId: string;
  /** Underlying governed artifact / scenario-run id (proof lineage sourceId). */
  evidenceSourceId: string;
  /** Source class of the governed artifact or run, when carried. */
  sourceClass: string | null;
  /** As-of of the governed readback (ISO date), when carried. */
  asOf: string | null;
  /** Instrument-panel provenance string (engine + evidence base + as-of). */
  engineProvenance: string;
}>;

/** A governed lever admitted as candidate input (adapted from the lever stack). */
export type PlanCandidateLeverInput = Readonly<{
  leverId: string;
  moduleId: string;
  /** Redaction-safe business label, e.g. "SG&A optimization". */
  label: string;
  /** Bound headline opportunity in USD (governed readback — never recomputed). */
  sizedUsd: number;
  certaintyClass: PlanCandidateCertaintyClass;
  /**
   * Fixed evidence-family key from the tenant registry (deconfliction
   * contract). `null` = family not declared: the lever may stand alone but can
   * never enter a combination (fail-closed — an undeclared family cannot prove
   * its dollars are separate).
   */
  evidenceFamily: string | null;
  /** Governed time-to-impact class, or null (named gap, not zero). */
  timeToImpactClass: PlanTimeToImpactClass | null;
  approvalOwnerClass: PlanApprovalOwnerClass;
  provenance: PlanCandidateLeverProvenance;
  constraintFlags?: readonly PlanCandidateConstraintFlag[];
}>;

export type PlanCandidateGapCode =
  | 'time_to_impact_unbound'
  | 'constraint_violated'
  | 'constraint_unevaluated';

export type PlanCandidateGap = Readonly<{
  code: PlanCandidateGapCode;
  label: string;
  leverRefs: readonly string[];
  constraintId?: string;
}>;

export type PlanCandidateProvenance = Readonly<{
  evidenceRefIds: readonly string[];
  evidenceSourceIds: readonly string[];
  sourceClasses: readonly string[];
  asOf: string | null;
}>;

/** One enumerated candidate path (a single lever or a legal combination). */
export type PlanCandidatePath = Readonly<{
  /** Stable id: `path:` + sorted lever ids joined with `+`. */
  candidateId: string;
  /** Sorted lever ids composing the path. */
  leverRefs: readonly string[];
  leverLabels: readonly string[];
  /**
   * Sum of the levers' governed headline values. Legal ONLY because every
   * lever draws from a distinct declared evidence family (no double-counting).
   */
  totalUsd: number;
  /** Weakest lever's class — the max-degradation law. */
  certaintyClass: PlanCandidateCertaintyClass;
  /** Counted class mix (never estimated). */
  certaintyMix: Readonly<Record<PlanCandidateCertaintyClass, number>>;
  /** Slowest lever's class, or null when ANY lever's window is unbound. */
  timeToImpactClass: PlanTimeToImpactClass | null;
  evidenceFamilies: readonly string[];
  approvalOwnerClasses: readonly PlanApprovalOwnerClass[];
  provenance: PlanCandidateProvenance;
  constraintFlags: readonly PlanCandidateConstraintFlag[];
  gaps: readonly PlanCandidateGap[];
}>;

export type PlanCandidateExclusionCode =
  | 'shared_evidence_family'
  | 'undeclared_evidence_family'
  | 'exceeds_max_levers';

export type PlanCandidateExclusion = Readonly<{
  code: PlanCandidateExclusionCode;
  leverRefs: readonly string[];
  detail: string;
  /** The colliding family for `shared_evidence_family` exclusions. */
  evidenceFamily?: string;
}>;

export type PlanCandidateSetStatus = 'enumerated' | 'no_admitted_levers';

export type PlanCandidateSet = Readonly<{
  status: PlanCandidateSetStatus;
  objectiveGapUsd: number | null;
  admittedLeverCount: number;
  /** Candidates in deterministic enumeration order (size, then id). */
  candidates: readonly PlanCandidatePath[];
  /** Named exclusions — the dedup ledger. Never silently dropped. */
  excluded: readonly PlanCandidateExclusion[];
  singleCount: number;
  comboCount: number;
}>;

export type EnumeratePlanCandidatesInput = Readonly<{
  objectiveGapUsd: number | null;
  levers: readonly PlanCandidateLeverInput[];
  /** Policy bound on combination size. Clamped to [1, 3] — never wider. */
  maxLeversPerCandidate: number;
}>;

const MAX_SUPPORTED_LEVERS_PER_CANDIDATE = 3;

export function planCandidateId(leverIds: readonly string[]): string {
  return `path:${[...leverIds].sort().join('+')}`;
}

/**
 * Enumerates every legal candidate path from the admitted lever set: singles
 * always; pairs/triples only when every member declares a distinct evidence
 * family (the deconfliction contract) and the policy bound admits the size.
 */
export function enumeratePlanCandidates(
  input: EnumeratePlanCandidatesInput,
): PlanCandidateSet {
  const levers = dedupeAndSortLevers(input.levers);
  const maxLevers = clampMaxLevers(input.maxLeversPerCandidate);

  if (levers.length === 0) {
    return Object.freeze({
      status: 'no_admitted_levers',
      objectiveGapUsd: input.objectiveGapUsd,
      admittedLeverCount: 0,
      candidates: Object.freeze([]),
      excluded: Object.freeze([]),
      singleCount: 0,
      comboCount: 0,
    });
  }

  const candidates: PlanCandidatePath[] = [];
  const excluded: PlanCandidateExclusion[] = [];

  for (const combination of enumerateCombinations(levers, maxLevers)) {
    if (combination.length === 1) {
      candidates.push(buildCandidate(combination));
      continue;
    }
    const legality = combinationLegality(combination);
    if (legality) {
      excluded.push(legality);
      continue;
    }
    candidates.push(buildCandidate(combination));
  }

  if (levers.length > maxLevers) {
    excluded.push(
      Object.freeze({
        code: 'exceeds_max_levers' as const,
        leverRefs: Object.freeze(levers.map((lever) => lever.leverId)),
        detail:
          `Combinations above ${maxLevers} levers are policy-bounded out; ` +
          'wider bundles are a portfolio question, not a candidate path.',
      }),
    );
  }

  const singleCount = candidates.filter((c) => c.leverRefs.length === 1).length;
  return Object.freeze({
    status: 'enumerated',
    objectiveGapUsd: input.objectiveGapUsd,
    admittedLeverCount: levers.length,
    candidates: Object.freeze(candidates),
    excluded: Object.freeze(excluded),
    singleCount,
    comboCount: candidates.length - singleCount,
  });
}

function clampMaxLevers(requested: number): number {
  if (!Number.isFinite(requested)) return MAX_SUPPORTED_LEVERS_PER_CANDIDATE;
  const floored = Math.floor(requested);
  if (floored < 1) return 1;
  if (floored > MAX_SUPPORTED_LEVERS_PER_CANDIDATE) {
    return MAX_SUPPORTED_LEVERS_PER_CANDIDATE;
  }
  return floored;
}

function dedupeAndSortLevers(
  levers: readonly PlanCandidateLeverInput[],
): readonly PlanCandidateLeverInput[] {
  const byId = new Map<string, PlanCandidateLeverInput>();
  for (const lever of levers) {
    if (!lever.leverId.trim()) continue;
    if (!Number.isFinite(lever.sizedUsd)) continue;
    if (!byId.has(lever.leverId)) byId.set(lever.leverId, lever);
  }
  return [...byId.values()].sort((a, b) => a.leverId.localeCompare(b.leverId));
}

/** Yields all 1..maxSize combinations in deterministic (size, lexical) order. */
function* enumerateCombinations(
  levers: readonly PlanCandidateLeverInput[],
  maxSize: number,
): Generator<readonly PlanCandidateLeverInput[]> {
  const n = levers.length;
  for (let size = 1; size <= Math.min(maxSize, n); size += 1) {
    const indexes = Array.from({ length: size }, (_, i) => i);
    while (true) {
      yield indexes.map((i) => levers[i]);
      let cursor = size - 1;
      while (cursor >= 0 && indexes[cursor] === n - size + cursor) cursor -= 1;
      if (cursor < 0) break;
      indexes[cursor] += 1;
      for (let next = cursor + 1; next < size; next += 1) {
        indexes[next] = indexes[next - 1] + 1;
      }
    }
  }
}

function combinationLegality(
  combination: readonly PlanCandidateLeverInput[],
): PlanCandidateExclusion | null {
  const undeclared = combination.filter((lever) => !lever.evidenceFamily);
  if (undeclared.length > 0) {
    return Object.freeze({
      code: 'undeclared_evidence_family' as const,
      leverRefs: Object.freeze(combination.map((lever) => lever.leverId)),
      detail:
        `${undeclared.map((lever) => lever.leverId).join(', ')} ` +
        'declares no evidence family; without a declared family the lever ' +
        'cannot prove its dollars are separate, so it stands alone only.',
    });
  }
  const byFamily = new Map<string, string[]>();
  for (const lever of combination) {
    const family = lever.evidenceFamily as string;
    byFamily.set(family, [...(byFamily.get(family) ?? []), lever.leverId]);
  }
  for (const [family, members] of byFamily) {
    if (members.length > 1) {
      return Object.freeze({
        code: 'shared_evidence_family' as const,
        leverRefs: Object.freeze(combination.map((lever) => lever.leverId)),
        evidenceFamily: family,
        detail:
          `${members.join(' + ')} draw from the same evidence family ` +
          `(${family}); summing them would double-count the same dollars, ` +
          'so the combination is withheld (deconfliction contract).',
      });
    }
  }
  return null;
}

function buildCandidate(
  combination: readonly PlanCandidateLeverInput[],
): PlanCandidatePath {
  const sorted = [...combination].sort((a, b) =>
    a.leverId.localeCompare(b.leverId),
  );
  const leverRefs = sorted.map((lever) => lever.leverId);
  const totalUsd = sorted.reduce((sum, lever) => sum + lever.sizedUsd, 0);
  const certaintyMix: Record<PlanCandidateCertaintyClass, number> = {
    certified: 0,
    scenario: 0,
    modeled: 0,
  };
  for (const lever of sorted) certaintyMix[lever.certaintyClass] += 1;

  const gaps: PlanCandidateGap[] = [];
  const unboundWindows = sorted.filter(
    (lever) => lever.timeToImpactClass === null,
  );
  if (unboundWindows.length > 0) {
    gaps.push(
      Object.freeze({
        code: 'time_to_impact_unbound' as const,
        label:
          'Time-to-impact window is not governed for ' +
          `${unboundWindows.map((lever) => lever.leverId).join(', ')}; ` +
          'the class is a named gap, never a default.',
        leverRefs: Object.freeze(unboundWindows.map((lever) => lever.leverId)),
      }),
    );
  }

  const constraintFlags = sorted.flatMap(
    (lever) => lever.constraintFlags ?? [],
  );
  for (const flag of constraintFlags) {
    if (flag.status === 'violated') {
      gaps.push(
        Object.freeze({
          code: 'constraint_violated' as const,
          label:
            `Constraint ${flag.constraintId} (${flag.resourceKind}) is ` +
            'violated; the candidate is marked, not dropped — the violation ' +
            'is content.',
          leverRefs: Object.freeze(leverRefs),
          constraintId: flag.constraintId,
        }),
      );
    } else if (flag.status === 'unevaluated') {
      gaps.push(
        Object.freeze({
          code: 'constraint_unevaluated' as const,
          label:
            `Constraint ${flag.constraintId} (${flag.resourceKind}) has no ` +
            'evaluation for this candidate; compliance is unknown by name.',
          leverRefs: Object.freeze(leverRefs),
          constraintId: flag.constraintId,
        }),
      );
    }
  }

  return Object.freeze({
    candidateId: planCandidateId(leverRefs),
    leverRefs: Object.freeze(leverRefs),
    leverLabels: Object.freeze(sorted.map((lever) => lever.label)),
    totalUsd,
    certaintyClass: weakestCertaintyClass(sorted),
    certaintyMix: Object.freeze(certaintyMix),
    timeToImpactClass:
      unboundWindows.length > 0 ? null : slowestTimeToImpactClass(sorted),
    evidenceFamilies: Object.freeze(
      uniqueStrings(sorted.map((lever) => lever.evidenceFamily)),
    ),
    approvalOwnerClasses: Object.freeze(
      uniqueOwnerClasses(sorted.map((lever) => lever.approvalOwnerClass)),
    ),
    provenance: Object.freeze({
      evidenceRefIds: Object.freeze(
        uniqueStrings(sorted.map((lever) => lever.provenance.evidenceRefId)),
      ),
      evidenceSourceIds: Object.freeze(
        uniqueStrings(sorted.map((lever) => lever.provenance.evidenceSourceId)),
      ),
      sourceClasses: Object.freeze(
        uniqueStrings(sorted.map((lever) => lever.provenance.sourceClass)),
      ),
      asOf: latestIsoDate(sorted.map((lever) => lever.provenance.asOf)),
    }),
    constraintFlags: Object.freeze(constraintFlags),
    gaps: Object.freeze(gaps),
  });
}

export function weakestCertaintyClass(
  levers: readonly Pick<PlanCandidateLeverInput, 'certaintyClass'>[],
): PlanCandidateCertaintyClass {
  let weakestIndex = PLAN_CANDIDATE_CERTAINTY_ORDER.length - 1;
  for (const lever of levers) {
    const index = PLAN_CANDIDATE_CERTAINTY_ORDER.indexOf(lever.certaintyClass);
    if (index < weakestIndex) weakestIndex = index;
  }
  return PLAN_CANDIDATE_CERTAINTY_ORDER[weakestIndex];
}

function slowestTimeToImpactClass(
  levers: readonly Pick<PlanCandidateLeverInput, 'timeToImpactClass'>[],
): PlanTimeToImpactClass | null {
  let slowestIndex = -1;
  for (const lever of levers) {
    if (lever.timeToImpactClass === null) return null;
    const index = PLAN_TIME_TO_IMPACT_ORDER.indexOf(lever.timeToImpactClass);
    if (index > slowestIndex) slowestIndex = index;
  }
  return slowestIndex >= 0 ? PLAN_TIME_TO_IMPACT_ORDER[slowestIndex] : null;
}

function uniqueStrings(
  values: readonly (string | null | undefined)[],
): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => value?.trim() ?? '')
        .filter((value) => value.length > 0),
    ),
  ).sort();
}

function uniqueOwnerClasses(
  values: readonly PlanApprovalOwnerClass[],
): PlanApprovalOwnerClass[] {
  return Array.from(new Set(values)).sort();
}

function latestIsoDate(values: readonly (string | null)[]): string | null {
  const dates = values.filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  if (dates.length === 0) return null;
  return [...dates].sort().at(-1) ?? null;
}
